import React, {useEffect, useState} from 'react';
import {requestDashboard, SESSION_EXPIRED_EVENT} from '../../api/api';

interface Proof {
  orderId: string; provider: string; status: string; amount: number; currency: string;
  originalName: string; uploadedAt: string;
}

export function PaymentProofs({apiBase, token, reservationId}: {apiBase: string; token: string; reservationId: string}) {
  const [proofs, setProofs] = useState<Proof[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [selected, setSelected] = useState<Proof | null>(null);
  const [preview, setPreview] = useState<{url: string; type: string} | null>(null);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    requestDashboard<Proof[]>({apiBase, token, withAuth: true, path: `/payments/reservation/${encodeURIComponent(reservationId)}/proofs`})
      .then(data => {if (active) setProofs(data);})
      .catch(error => {if (active) setError(error.message);})
      .finally(() => {if (active) setLoading(false);});
    return () => {active = false;};
  }, [apiBase, token, reservationId, refresh]);

  useEffect(() => {
    if (!selected) return;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30000);
    let active = true;
    let objectUrl: string | undefined;
    setPreview(null); setOpening(true); setError('');
    (async () => {
      const response = await fetch(`${apiBase}/payments/${encodeURIComponent(selected.orderId)}/proof`, {
        headers: {Authorization: `Bearer ${token}`}, signal: controller.signal,
      });
      if (response.status === 401) window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      if (!response.ok) throw new Error('Unable to load receipt. Check your access and try again.');
      const blob = await response.blob();
      if (!['application/pdf', 'image/jpeg', 'image/png', 'image/webp'].includes(blob.type)) throw new Error('Receipt format is not supported for preview. Please update the API.');
      if (!active) return;
      objectUrl = URL.createObjectURL(blob);
      setPreview({url: objectUrl, type: blob.type});
    })().catch(error => {if (active) setError(error.name === 'AbortError' ? 'Receipt loading timed out. Please try again.' : error.message);})
      .finally(() => {clearTimeout(timer); if (active) setOpening(false);});
    return () => {active = false; clearTimeout(timer); controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl);};
  }, [selected, apiBase, token]);

  return <section className='mb-6 rounded-lg border border-blue-100 p-4' aria-label='Payment proofs'>
    <div className='flex items-center justify-between'><h4 className='font-semibold'>Payment proofs</h4>
      <button type='button' disabled={loading} onClick={() => setRefresh(value => value + 1)}>Refresh receipts</button></div>
    {loading ? <p role='status'>Loading receipts…</p> : proofs.length === 0 && !error ? <p className='mt-2 text-sm text-gray-500'>No proof of payment has been uploaded for this reservation.</p> : null}
    {error && <p role='alert' className='mt-2 text-red-600'>{error}</p>}
    {proofs.map(proof => <div key={proof.orderId} className='mt-3 rounded border p-3'>
      <p className='break-all font-medium'>{proof.originalName}</p>
      <p className='text-sm'>{proof.currency} {proof.amount.toLocaleString()} · {proof.provider === 'manual' ? 'Manual payment' : 'Online payment'} · {proof.status === 'pending' && proof.provider === 'manual' ? 'Awaiting verification' : proof.status}</p>
      <p className='text-xs text-gray-500'>{new Date(proof.uploadedAt).toLocaleString()}</p>
      <button type='button' className='mt-2 text-blue-700 underline' onClick={() => setSelected({...proof})}>View receipt</button>
    </div>)}
    {opening && <p role='status'>Opening receipt…</p>}
    {preview && selected && <div className='mt-4'>
      <div className='mb-3 flex gap-4'><a href={preview.url} download={selected.originalName} className='text-blue-700 underline'>Download receipt</a>
        <button type='button' onClick={() => {setSelected(null); setPreview(null);}}>Close preview</button></div>
      {preview.type === 'application/pdf' ? <iframe title='Payment receipt PDF' src={preview.url} className='h-96 w-full rounded border' /> : <img src={preview.url} alt='Uploaded payment receipt' className='max-h-96 max-w-full rounded object-contain' />}
    </div>}
  </section>;
}
